import React, { useEffect, type FunctionComponent } from "react";
import { Checkbox, Form, Input, InputNumber } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { DEFAULT_VAT_RATE } from "../constants";
import { useVatIssuer, type InvoiceFormController } from "../hooks/useVatIssuer";
import { type IFirmaIntegrationFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<IFirmaIntegrationFormModel>;
  onSubmit: (formData: IFirmaIntegrationFormModel) => void;
  model?: IFirmaIntegrationFormModel | null;
}

type Props = OwnProps;

const IFirmaIntegrationForm: FunctionComponent<Props> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation(["settings", "common"]);
  const { isVatIssuer, onChangeVatIssuerField } = useVatIssuer(
    model?.settings?.isVatIssuer,
    formController as InvoiceFormController,
  );

  useEffect(() => {
    if (!model) {
      formController?.resetFields();
    } else {
      formController?.setFieldsValue(model);
    }
  }, [model]);

  return (
    <div className="min-w-[450px]">
      <Form<IFirmaIntegrationFormModel>
        name="invoice-integration-form"
        layout="vertical"
        form={formController}
        onFinish={onSubmit}
        initialValues={{
          url: model?.url ?? "",
          settings: {
            isVatIssuer,
            defaultVatRate: DEFAULT_VAT_RATE,
          },
        }}
        validateTrigger={["onSubmit"]}
      >
        <Form.Item
          name="accountName"
          label={t("invoices.accountName")}
          tooltip={t("invoices.accountNameTooltip")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input
            autoFocus={!model}
            disabled={!!model?.url}
            placeholder={t<string>("invoices.placeholder.accountName")}
          />
        </Form.Item>
        <Form.Item
          name="url"
          label={t("invoices.login")}
          tooltip={t("invoices.ifirma.loginTooltip")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder={t<string>("invoices.placeholder.login")} />
        </Form.Item>

        <Form.Item
          name="apiKey"
          tooltip={t("invoices.ifirma.apiKeyTooltip")}
          label={t("invoices.ifirma.apiKey")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input autoFocus={!!model} />
        </Form.Item>

        <Form.Item
          name="accountApiKey"
          label={t("invoices.ifirma.accountApiKey")}
          tooltip={t("invoices.ifirma.accountApiKeyTooltip")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item name={["settings", "isVatIssuer"]} valuePropName="checked">
          <Checkbox onChange={(e) => onChangeVatIssuerField(e.target.checked)}>{t("invoices.isVatIssuer")}</Checkbox>
        </Form.Item>

        {isVatIssuer ? (
          <Form.Item
            label={t("invoices.defaultVatRate")}
            name={["settings", "defaultVatRate"]}
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <InputNumber<string | number>
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value?.replace("%", "") ?? 0}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("invoices.legalVATExemption")}
            name={["settings", "legalVatExemption"]}
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
              {
                max: 30,
                message: t<string>("common:validationErrors.maxLength", {
                  number: 30,
                }),
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default IFirmaIntegrationForm;
